import type { MaybeRefOrGetter } from 'vue'
import { computed, ref, toValue } from 'vue'
import { getAxiosResponseStatus } from '~/utils/axios'
import { EMPTY_METRICS_BLOCK, EMPTY_SEO_BLOCK } from '~/constants/empty'
import type { ProfessionCardObj, ProfessionData, ProfessionDataResponse } from '~/types/profession'
import type { MetricsBlock, ProgramCardInfos, SeoSiblingsBlock } from '~/types/school'
import { parseBlock } from '~/utils/helpers'

interface UseProfessionDataOptions {
  language: MaybeRefOrGetter<string>
  alias?: MaybeRefOrGetter<string | undefined>
}

/**
 * Construit l'URL d'embed YouTube à partir d'une URL classique.
 * @param {string} youtubeUrl URL YouTube (watch/youtu.be/embed).
 * @returns {string} URL d'embed (ou chaîne vide si invalide).
 */
function getYouTubeEmbedUrl(youtubeUrl: string): string {
  const trimmed: string = youtubeUrl.trim()
  if (!trimmed) return ''

  const normalized: string = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  let videoId = ''
  try {
    if (normalized.includes('youtube.com/watch')) {
      videoId = new URL(normalized).searchParams.get('v') ?? ''
    } else if (normalized.includes('youtu.be')) {
      videoId = normalized.split('/').pop()?.split('?')[0] ?? ''
    } else if (normalized.includes('youtube.com/embed/')) {
      videoId = normalized.split('/').pop()?.split('?')[0] ?? ''
    }
  } catch {
    return ''
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : ''
}

/**
 * Charge la fiche métier via `/api/profession/professiondata`.
 * @param {UseProfessionDataOptions} options Langue + alias (slug du métier).
 * @returns {Promise<{ professionData: Ref<ProfessionDataResponse | null>; loading: ComputedRef<boolean>; error: ComputedRef<string | null>; lastHttpStatus: ComputedRef<number | undefined>; refresh: () => Promise<void> }>} Données réactives, état de chargement et erreur éventuelle.
 */
export const useProfessionData = async (options: UseProfessionDataOptions) => {
  const api = useFrontApi()
  const lastHttpStatus = ref<number | undefined>(undefined)

  const {
    data: professionData,
    error,
    status,
    refresh,
  } = await useAsyncData<ProfessionDataResponse | null>(
    () => `profession-data-${toValue(options.language)}-${toValue(options.alias) ?? ''}`,
    async () => {
      const language = toValue(options.language)
      const alias = toValue(options.alias)

      if (!alias || !language) {
        lastHttpStatus.value = undefined
        return null
      }

      try {
        const endpoint: string = `/api/profession/professiondata?language=${language}&alias=${alias}`
        const { data } = await api.get<ProfessionData>(endpoint)

        lastHttpStatus.value = 200
        data.videoUrls = (data.videoUrls ?? []).map((url: string) => getYouTubeEmbedUrl(url))

        return {
          professiondatas: data,
          parsedParentBlock: parseBlock<SeoSiblingsBlock>(data.parentBlock, EMPTY_SEO_BLOCK),
          parsedInterestsBlock: parseBlock<SeoSiblingsBlock>(data.interestsBlock, EMPTY_SEO_BLOCK),
          parsedRelatedProfessions: parseBlock<ProfessionCardObj[]>(data.relatedProfessions, []),
          parsedMetricsBlock: parseBlock<MetricsBlock>(data.metricsBlock, EMPTY_METRICS_BLOCK),
          parsedPartners: parseBlock<ProgramCardInfos[]>(data.partners, []),
        }
      } catch (e) {
        lastHttpStatus.value = getAxiosResponseStatus(e)
        return null
      }
    },
    {
      watch: [() => toValue(options.language), () => toValue(options.alias)],
    },
  )

  return {
    professionData,
    loading: computed(() => status.value === 'pending'),
    error: computed(() => (error.value ? (error.value.message ?? 'Erreur inconnue') : null)),
    lastHttpStatus: computed(() => lastHttpStatus.value),
    refresh,
  }
}
